<template>
  <nav class="h-16 px-8 flex items-center bg-transparent w-full flex-none">
    <div
      class="flex items-center gap-4 relative w-full justify-between"
      ref="tabsRef"
    >
      <!-- TABS -->
      <router-link
        v-for="(tab, index) in tabs"
        :key="tab.name"
        :to="tab.route"
        class="w-10 h-10 flex items-center justify-center rounded transition-colors !duration-500 z-10 relative"
        :class="[
          routeIndex === index
            ? `text-primary-contrast ${
                animationRunning
                  ? 'bg-transparent'
                  : 'bg-primary transition-none'
              }`
            : 'text-gray-6 transition-text-color',
        ]"
      >
        <font-awesome-icon :icon="['fas', tab.icon]" />
        <!-- BADGE -->
        <span
          v-if="tab.badge && badges[tab.name]"
          class="absolute -top-1 left-[calc(100%-10px)] h-4 min-w-4 rounded-full z-10 text-center px-1 text-2xs flex items-center border border-white bg-red text-white font-bold"
        >
          {{ badges[tab.name] }}
        </span>
      </router-link>
      <!-- ACTIVE BACKGROUND -->
      <div
        class="w-10 h-10 rounded absolute transition-transform bg-primary"
        :class="{ 'opacity-0': !animationRunning }"
        :style="[activeRoute]"
      ></div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { useNotificationStore } from '@getmayday/vue-modules';
import { useFeedbackStore } from '@getmayday/vue-modules';

const configStore = useConfigStore();

const notificationStore = useNotificationStore();
const { unreadCountDisplay } = storeToRefs(notificationStore);

const feedbackStore = useFeedbackStore();
const { unreadCommentCount } = storeToRefs(feedbackStore);

//const { appSectionView } = useLogs();

const tabs = computed(() => [
  {
    icon: 'book',
    name: 'knowledge',
    route: '/',
  },
  { icon: 'star', name: 'favorites', route: '/favorites' },
  {
    icon: 'bell',
    name: 'notifications',
    route: '/notifications',
    badge: true,
  },
  {
    icon: 'megaphone',
    name: 'feedbacks',
    route: '/feedbacks?source=feedback_section',
    badge: true,
  },
  { icon: 'cog', name: 'settings', route: '/settings' },
]);

const badges = computed<Record<string, string | null>>(() => {
  return {
    notifications: unreadCountDisplay.value,
    feedbacks:
      unreadCommentCount.value > 0 ? String(unreadCommentCount.value) : null,
  };
});

// CHANGE ROUTE ANIMATION
const TAB_WIDTH = 40;
const ANIMATION_DURATION = 500;
const route = useRoute();

const tabsRef = ref<HTMLElement | null>('tabsRef');

const routeIndex = computed(() => {
  if (route.meta.tabIndex === undefined) return -1;
  return tabs.value.findIndex((_tab, index) => index === route.meta.tabIndex);
});

const animationDuration = ref(ANIMATION_DURATION);
const animationRunning = ref(false);

const computedAnimationDuration = computed(() => {
  return animationDuration.value;
});

const activeRoute = computed(() => {
  animationRunning.value = true;
  setTimeout(() => {
    animationRunning.value = false;
  }, computedAnimationDuration.value);

  if (routeIndex.value === -1) return 'transform: translateX(-200%)';
  //calculate the gap between tabs by using the tabsRef.value, the number of tabs and the width of each tab
  const GAP =
    (tabsRef.value?.offsetWidth - tabs.value.length * TAB_WIDTH) /
    (tabs.value.length - 1);

  return `transform: translateX(${
    routeIndex.value * (TAB_WIDTH + GAP)
  }px); transition-duration: ${computedAnimationDuration.value}ms !important;`;
});

watch(routeIndex, (newValue) => {
  const sectionName = tabs.value[newValue]?.name;
  //appSectionView(sectionName.toUpperCase());
});
</script>

<style lang="postcss" scoped>
.router-link-active {
  color: theme('colors.primary.contrast');
}

.shadow-tabs {
  box-shadow: 0px -1px 10px rgba(112, 144, 176, 0.15);
}

.transition-text-color {
  transition-property: color;
  transition-duration: 500ms;
}
</style>
